import {type ThunkResult} from '../../types/redux';
import {isAndOrFiltersEnabled} from '../../util/andOrFiltersFeatureFlag';
import {redirectToSignupThenBackHere} from '../../util/auth';
import {parsedReportSpec, ReportSpecVersion} from '../../util/report';
import {displayError} from '../global/actions';
import * as ViewActions from '../views/actions';
import * as ViewActionsInternal from '../views/actionsInternal';
import * as ViewApi from '../views/api';
import * as ViewThunks from '../views/thunks';
import type * as Actions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as Types from './types';

export function load(
  startAction: ReturnType<typeof Actions.loadStarted>
): ThunkResult<void> {
  const {id, reportID} = startAction.payload;
  return async (dispatch, getState, client) => {
    dispatch(startAction); // start loading for "reports" reducer
    dispatch(ViewActionsInternal.loadStarted()); // start loading for "views" reducer
    dispatch(ViewActionsInternal.clearReportViews());

    try {
      const result = await ViewApi.loadSingleViewById(
        client,
        reportID,
        'no-cache'
      );

      if (reportID !== result.id) {
        throw new Error('invalid state');
      }

      // We only enable panel comments if panels already have persistent IDs *before* the fromJSON migrations run.
      // This is because a) each comment needs to be associated with a persistent panel ID,
      // and b) we can only add/save panel IDs in report edit mode (since we can't write IDs to the view spec in read mode).
      // This has the unfortunate effect of disabling panel comments when viewing an older report (until the author re-saves the report),
      // but we decided this is preferable to doing a server-side view spec migration to add panel IDs.
      const panelCommentsEnabled =
        result.type === 'runs' &&
        result.spec.version != null &&
        result.spec.version >= ReportSpecVersion.AddPanelIds;

      // parse report view spec and apply any necessary migrations
      if (
        result.type !== Types.REPORT_VIEW_TYPE &&
        result.type !== Types.REPORT_DRAFT_VIEW_TYPE
      ) {
        throw new Error(`Loaded report has incorrect type: ${result.type}`);
      }
      const {spec: parsedSpec, isFiltersMigrated} = await parsedReportSpec(
        result.spec,
        result.type,
        client,
        isAndOrFiltersEnabled()
      );

      dispatch(
        ViewActionsInternal.loadFinished(
          result.cid,
          {...result, spec: parsedSpec},
          false,
          panelCommentsEnabled
        )
      );
      dispatch(
        ActionsInternal.loadFinished(id, {
          type: result.type,
          id: result.cid,
        })
      );

      if (isFiltersMigrated) {
        dispatch(ViewActions.save({type: result.type, id: result.cid}));
      }
    } catch (e: any) {
      if (typeof e !== 'string') {
        return;
      }

      if (e.match('not found')) {
        const urlParams = new URLSearchParams(window.location.search);
        // If `authRedirect` is set, redirect to the login page
        if (urlParams.get('authRedirect')) {
          // Redirect back to the current page after login (w/out query params)
          redirectToSignupThenBackHere(true);
        } else {
          dispatch(ActionsInternal.unloadNoMatch(id, reportID));
          dispatch(ViewActionsInternal.unloadNoMatch());
        }
      } else if (e.match('parse error')) {
        dispatch(displayError({code: 500, message: 'Error parsing report'}));
      }
    }
  };
}

export function unload(reportID: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    // Loads aren't always successful, so we might be in a bad state by this point.
    // TODO probably introduce checks everywhere we access the store by key like this.
    if (!(reportID in state.reports.viewRefs)) {
      return;
    }
    const viewRef = state.reports.viewRefs[reportID];

    dispatch(ActionsInternal.unloadReport(reportID));
    dispatch(ViewThunks.unloadView(viewRef.id));
    dispatch(ViewActionsInternal.deleteUndoRedoHistory());
  };
}
